import { useEffect, useState } from "react";
import { Button, Card, Col, Drawer, Input, message, Popconfirm, Row, Select, Skeleton, Table, Tag } from "antd";
import {
    PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined, CloseOutlined,
} from "@ant-design/icons";
import { Search } from "lucide-react";
import dayjs from "dayjs";
import Layout from '../../Layout'
import {
    getCampaignPrefixes,
    createCampaignPrefix,
    updateCampaignPrefix,
    deleteCampaignPrefix,
    getCampaignsByPrefix,
    CampaignPrefix,
} from "../../api/CampaignPrefix";
import { FbCampaign, FbAdSet, FbAd, FbInsights, getFbAdSets, getFbAds } from "../../api/FacebookAds";
import { useDebounce } from "../../hooks/useDebounce";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { TextArea } = Input;

const PER_PAGE = 20;
const CAMP_PER_PAGE = 50; // API by-prefix tối đa 50

const fmtMoney = (v: any) => (v === null || v === undefined || v === '' ? '—' : `${Number(v).toLocaleString('vi-VN')} đ`);
const fmtNum = (v: any) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('vi-VN'));
const fmtPct = (v: any) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);
const fmtDate = (v: any) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—');

// Lưới 12 chỉ số hiệu năng (dùng chung cho cả 3 cấp: tiền tố / nhóm QC / quảng cáo)
const buildMetrics = (ins?: FbInsights | null) => ([
    { label: 'HIỂN THỊ', value: fmtNum(ins?.impressions), color: 'bg-yellow-400' },
    { label: 'TIẾP CẬN', value: fmtNum(ins?.reach), color: 'bg-yellow-400' },
    { label: 'LƯỢT CLICK', value: fmtNum(ins?.clicks), color: 'bg-yellow-400' },
    { label: 'CHI PHÍ', value: fmtMoney(ins?.spend), color: 'bg-yellow-500', isCurrency: true },
    { label: 'CTR TRUNG BÌNH', value: fmtPct(ins?.ctr), color: 'bg-green-500' },
    { label: 'CPC TRUNG BÌNH', value: fmtMoney(ins?.cpc), color: 'bg-blue-500' },
    { label: 'CPM TRUNG BÌNH', value: fmtMoney(ins?.cpm), color: 'bg-yellow-400' },
    { label: 'TỔNG LEADS', value: fmtNum(ins?.leads), color: 'bg-green-500' },
    { label: 'CVR TRUNG BÌNH', value: fmtPct(ins?.cvr), color: 'bg-blue-500' },
    { label: 'CPL/CPA TRUNG BÌNH', value: fmtMoney(ins?.cpl), color: 'bg-red-500' },
    { label: 'DOANH THU', value: fmtMoney(ins?.revenue), color: 'bg-green-500' },
    { label: 'ROAS TRUNG BÌNH', value: ins?.roas === null || ins?.roas === undefined ? '—' : Number(ins.roas).toFixed(2), color: 'bg-yellow-500' },
]);

const MetricCard = ({ label, value, color, isCurrency }: any) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 relative overflow-hidden shadow-sm flex flex-col justify-center">
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${color}`}></div>
        <div className="pl-2">
            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
            <div className={`text-xl font-bold ${isCurrency ? 'text-[#3e91ab]' : 'text-gray-800'}`}>{value}</div>
        </div>
    </div>
);

const MetricGrid = ({ insights, loading }: { insights?: FbInsights | null; loading?: boolean }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {buildMetrics(insights).map((m) => (
            <MetricCard key={m.label} {...m} value={loading ? <SkeletonBar width="70%" height={22} /> : m.value} />
        ))}
    </div>
);

// Vệt xám nhấp nháy thay cho spinner khi đang tải.
// Bọc trong div đặt bề rộng vì Skeleton.Input là inline-block, nhận % width không đúng;
// dùng prop `block` để vệt xám kéo đầy div bọc ngoài.
const SkeletonBar = ({ width, height = 14 }: { width: string; height?: number }) => (
    <div style={{ width }}>
        <Skeleton.Input active size="small" block style={{ height, minWidth: 0 }} />
    </div>
);

// Khung skeleton mô phỏng vài dòng của bảng trong lúc chờ dữ liệu
const TableSkeleton = ({ rows = 6 }: { rows?: number }) => {
    const widths = ['26%', '12%', '16%', '14%', '18%', '10%'];
    return (
        <div className="rounded-lg border border-slate-100 overflow-hidden">
            {Array.from({ length: rows }).map((_, r) => (
                <div
                    key={r}
                    className={`flex items-center gap-4 px-4 py-3 ${r === 0 ? 'bg-slate-50' : 'border-t border-slate-100'}`}
                >
                    {widths.map((w, c) => (
                        <SkeletonBar key={c} width={w} />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default function CampaignPrefixPage() {
    const [prefix, setPrefix] = useState("");
    const [note, setNote] = useState("");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    const [data, setData] = useState<CampaignPrefix[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    // Lọc ngay khi gõ (không cần Enter), chờ 400ms cho ngưng gõ rồi mới gọi API
    const search = useDebounce(searchInput.trim(), 400);

    // Drawer xem chiến dịch theo tiền tố
    const [viewingPrefix, setViewingPrefix] = useState<CampaignPrefix | null>(null);
    const [campaigns, setCampaigns] = useState<FbCampaign[]>([]);
    const [campTotal, setCampTotal] = useState(0);
    const [campLoading, setCampLoading] = useState(false);
    const [campStatus, setCampStatus] = useState<string | undefined>(undefined);
    const [campSearchInput, setCampSearchInput] = useState("");
    const campSearch = useDebounce(campSearchInput.trim(), 400);
    const [campPage, setCampPage] = useState(1);

    // Drawer nhóm quảng cáo (adSets)
    const [selectedCampaign, setSelectedCampaign] = useState<FbCampaign | null>(null);
    const [adSets, setAdSets] = useState<FbAdSet[]>([]);
    const [adSetsTotal, setAdSetsTotal] = useState(0);
    const [adSetsSummary, setAdSetsSummary] = useState<FbInsights | null>(null);
    const [adSetsLoading, setAdSetsLoading] = useState(false);
    const [adSetsPage, setAdSetsPage] = useState(1);

    // Drawer quảng cáo (ads)
    const [selectedAdSet, setSelectedAdSet] = useState<FbAdSet | null>(null);
    const [ads, setAds] = useState<FbAd[]>([]);
    const [adsTotal, setAdsTotal] = useState(0);
    const [adsSummary, setAdsSummary] = useState<FbInsights | null>(null);
    const [adsLoading, setAdsLoading] = useState(false);
    const [adsPage, setAdsPage] = useState(1);

    const fetchList = (p = page, kw = search) => {
        setLoading(true);
        getCampaignPrefixes({ search: kw || undefined, per_page: PER_PAGE, page: p }).then((res) => {
            setData(res.status ? res.items : []);
            setTotal(res.status ? res.total : 0);
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchList(page, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search]);

    // Nạp chiến dịch theo tiền tố khi mở drawer / đổi bộ lọc
    useEffect(() => {
        if (!viewingPrefix) return;
        setCampLoading(true);
        getCampaignsByPrefix({
            prefix_id: viewingPrefix.id,
            status: campStatus,
            search: campSearch || undefined,
            per_page: CAMP_PER_PAGE,
            page: campPage,
        }).then((res) => {
            setCampaigns(res.status ? res.items : []);
            setCampTotal(res.status ? res.total : 0);
            setCampLoading(false);
        });
    }, [viewingPrefix, campStatus, campSearch, campPage]);

    // Nạp nhóm quảng cáo khi chọn chiến dịch
    useEffect(() => {
        if (!selectedCampaign) return;
        setAdSetsLoading(true);
        getFbAdSets({ campaign_id: selectedCampaign.id, per_page: 50, page: adSetsPage }).then((res) => {
            setAdSets(res.status ? res.items : []);
            setAdSetsTotal(res.status ? res.total : 0);
            setAdSetsSummary(res.summary_insights || null);
            setAdSetsLoading(false);
        });
    }, [selectedCampaign, adSetsPage]);

    // Nạp quảng cáo khi chọn nhóm
    useEffect(() => {
        if (!selectedAdSet) return;
        setAdsLoading(true);
        getFbAds({ ad_set_id: selectedAdSet.id, per_page: 50, page: adsPage }).then((res) => {
            setAds(res.status ? res.items : []);
            setAdsTotal(res.status ? res.total : 0);
            setAdsSummary(res.summary_insights || null);
            setAdsLoading(false);
        });
    }, [selectedAdSet, adsPage]);

    const resetForm = () => {
        setEditingId(null);
        setPrefix("");
        setNote("");
    };

    const handleSave = async () => {
        if (!prefix.trim()) {
            message.warning("Vui lòng nhập tiền tố.");
            return;
        }
        setSaving(true);
        const body = { prefix: prefix.trim(), description: note.trim() || undefined };
        const res = editingId
            ? await updateCampaignPrefix(editingId, body)
            : await createCampaignPrefix(body);
        setSaving(false);
        if (res.status) {
            message.success(res.msg || (editingId ? "Cập nhật thành công" : "Thêm thành công"));
            resetForm();
            setPage(1);
            fetchList(1, search);
        } else {
            message.error(res.msg || "Lưu tiền tố thất bại");
        }
    };

    const handleDelete = async (id: number) => {
        const res = await deleteCampaignPrefix(id);
        if (res.status) {
            message.success(res.msg || "Đã xóa.");
            if (editingId === id) resetForm();
            fetchList(page, search);
        } else {
            message.error(res.msg || "Xóa tiền tố thất bại");
        }
    };

    const handleEdit = (record: CampaignPrefix) => {
        setEditingId(record.id);
        setPrefix(record.prefix);
        setNote(record.description || "");
    };

    const handleView = (record: CampaignPrefix) => {
        setCampaigns([]);
        setCampTotal(0);
        setCampStatus(undefined);
        setCampSearchInput("");
        setCampPage(1);
        setViewingPrefix(record);
    };

    const handleCampaignClick = (campaign: FbCampaign) => {
        setAdSets([]);
        setAdSetsTotal(0);
        setAdSetsSummary(null);
        setAdSetsPage(1);
        setSelectedCampaign(campaign);
    };

    const handleAdSetClick = (adSet: FbAdSet) => {
        setAds([]);
        setAdsTotal(0);
        setAdsSummary(null);
        setAdsPage(1);
        setSelectedAdSet(adSet);
    };

    const columns = [
        {
            title: "Tiền tố",
            dataIndex: "prefix",
            width: 180,
            render: (value: string) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: "Mô tả",
            dataIndex: "description",
            render: (value: string) => value || <span className="text-slate-400">—</span>,
        },
        {
            title: "Ngày tạo",
            dataIndex: "created_at",
            width: 170,
            render: (value: string) => <span className="text-slate-500">{fmtDate(value)}</span>,
        },
        {
            key: "actions",
            title: "Thao tác",
            width: 180,
            align: "center" as const,
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: CampaignPrefix) => (
                <div className="flex justify-center gap-2">

                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        title="Xem chiến dịch theo tiền tố"
                        onClick={() => handleView(record)}
                    />

                    <Button
                        type="text"
                        icon={<EditOutlined className="text-000000e0" />}
                        onClick={() => handleEdit(record)}
                    />

                    <Popconfirm
                        title="Xóa tiền tố?"
                        okText="Đồng ý"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>

                </div>
            ),
        },
    ];

    // Bảng chiến dịch theo tiền tố (12 chỉ số insights từ API)
    const campaignColumns = [
        {
            title: "Tên chiến dịch",
            dataIndex: "name",
            width: 260,
            fixed: "left" as const,
            render: (_: any, r: FbCampaign) => (
                <div className="cursor-pointer" onClick={() => handleCampaignClick(r)}>
                    <div className="font-semibold text-sm text-[#3e91ab] hover:text-blue-700 whitespace-normal break-words">{r.name}</div>
                    <div className="text-xs text-slate-400">ID: {r.fb_campaign_id}</div>
                </div>
            ),
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            width: 120,
            render: (v: string) => (
                <Tag color={v === 'ACTIVE' ? 'success' : v === 'PAUSED' ? 'warning' : 'default'} bordered={false}>
                    {v === 'ACTIVE' ? 'ĐANG CHẠY' : v === 'PAUSED' ? 'TẠM DỪNG' : v || '—'}
                </Tag>
            ),
        },
        { key: "impressions", title: "Hiển thị", width: 110, render: (_: any, r: FbCampaign) => fmtNum(r.insights?.impressions) },
        { key: "reach", title: "Tiếp cận", width: 110, render: (_: any, r: FbCampaign) => fmtNum(r.insights?.reach) },
        { key: "clicks", title: "Lượt click", width: 110, render: (_: any, r: FbCampaign) => fmtNum(r.insights?.clicks) },
        { key: "spend", title: "Chi phí", width: 140, render: (_: any, r: FbCampaign) => <span className="font-semibold text-yellow-600">{fmtMoney(r.insights?.spend)}</span> },
        { key: "ctr", title: "CTR", width: 90, render: (_: any, r: FbCampaign) => <span className="text-green-600">{fmtPct(r.insights?.ctr)}</span> },
        { key: "cpc", title: "CPC", width: 120, render: (_: any, r: FbCampaign) => <span className="text-blue-600">{fmtMoney(r.insights?.cpc)}</span> },
        { key: "cpm", title: "CPM", width: 130, render: (_: any, r: FbCampaign) => fmtMoney(r.insights?.cpm) },
        { key: "leads", title: "Leads", width: 90, render: (_: any, r: FbCampaign) => <span className="text-green-600 font-semibold">{fmtNum(r.insights?.leads)}</span> },
        { key: "cvr", title: "CVR", width: 90, render: (_: any, r: FbCampaign) => <span className="text-blue-600">{fmtPct(r.insights?.cvr)}</span> },
        { key: "cpl", title: "CPL/CPA", width: 130, render: (_: any, r: FbCampaign) => <span className="text-red-600">{fmtMoney(r.insights?.cpl)}</span> },
        { key: "revenue", title: "Doanh thu", width: 140, render: (_: any, r: FbCampaign) => <span className="text-green-600">{fmtMoney(r.insights?.revenue)}</span> },
        { key: "roas", title: "ROAS", width: 90, render: (_: any, r: FbCampaign) => (r.insights?.roas === null || r.insights?.roas === undefined ? '—' : Number(r.insights.roas).toFixed(2)) },
    ];

    const adSetColumns = [
        {
            title: "Tên nhóm QC",
            dataIndex: "name",
            width: 250,
            fixed: "left" as const,
            render: (_: any, r: FbAdSet) => (
                <div className="cursor-pointer" onClick={() => handleAdSetClick(r)}>
                    <div className="font-semibold text-sm text-[#3e91ab] hover:text-blue-700 whitespace-normal break-words">{r.name}</div>
                    <div className="text-xs text-slate-400">ID: {r.fb_ad_set_id}</div>
                </div>
            ),
        },
        {
            title: "Trạng thái", dataIndex: "status", width: 120,
            render: (v: string) => <Tag color={v === 'ACTIVE' ? 'success' : v === 'PAUSED' ? 'warning' : 'default'} bordered={false}>{v === 'ACTIVE' ? 'ĐANG CHẠY' : v === 'PAUSED' ? 'TẠM DẮNG' : v || '—'}</Tag>,
        },
        { key: "optimization_goal", title: "Mục tiêu", width: 160, render: (_: any, r: FbAdSet) => r.optimization_goal || '—' },
        { key: "daily_budget", title: "NS ngày", width: 120, render: (_: any, r: FbAdSet) => fmtMoney(r.daily_budget) },
        { key: "lifetime_budget", title: "NS trọn đời", width: 130, render: (_: any, r: FbAdSet) => fmtMoney(r.lifetime_budget) },
        { key: "impressions", title: "Hiển thị", width: 100, render: (_: any, r: FbAdSet) => fmtNum(r.insights?.impressions) },
        { key: "reach", title: "Tiếp cận", width: 100, render: (_: any, r: FbAdSet) => fmtNum(r.insights?.reach) },
        { key: "clicks", title: "Lượt click", width: 100, render: (_: any, r: FbAdSet) => fmtNum(r.insights?.clicks) },
        { key: "spend", title: "Chi phí", width: 140, render: (_: any, r: FbAdSet) => <span className="font-semibold text-yellow-600">{fmtMoney(r.insights?.spend)}</span> },
        { key: "ctr", title: "CTR", width: 80, render: (_: any, r: FbAdSet) => <span className="text-green-600">{fmtPct(r.insights?.ctr)}</span> },
        { key: "cpc", title: "CPC", width: 120, render: (_: any, r: FbAdSet) => <span className="text-blue-600">{fmtMoney(r.insights?.cpc)}</span> },
        { key: "cpm", title: "CPM", width: 120, render: (_: any, r: FbAdSet) => fmtMoney(r.insights?.cpm) },
        { key: "leads", title: "Leads", width: 80, render: (_: any, r: FbAdSet) => <span className="text-green-600 font-semibold">{fmtNum(r.insights?.leads)}</span> },
        { key: "cvr", title: "CVR", width: 80, render: (_: any, r: FbAdSet) => <span className="text-blue-600">{fmtPct(r.insights?.cvr)}</span> },
        { key: "cpl", title: "CPL/CPA", width: 130, render: (_: any, r: FbAdSet) => <span className="text-red-600">{fmtMoney(r.insights?.cpl)}</span> },
        { key: "revenue", title: "Doanh thu", width: 130, render: (_: any, r: FbAdSet) => <span className="text-green-600">{fmtMoney(r.insights?.revenue)}</span> },
        { key: "roas", title: "ROAS", width: 80, render: (_: any, r: FbAdSet) => r.insights?.roas == null ? '—' : Number(r.insights.roas).toFixed(2) },
    ];

    const adColumns = [
        {
            title: "Tên quảng cáo",
            dataIndex: "name",
            width: 250,
            fixed: "left" as const,
            render: (_: any, r: FbAd) => (
                <div>
                    <div className="font-semibold text-sm text-gray-800 whitespace-normal break-words">{r.name}</div>
                    <div className="text-xs text-slate-400">ID: {r.fb_ad_id}</div>
                </div>
            ),
        },
        {
            title: "Trạng thái", dataIndex: "status", width: 120,
            render: (v: string) => <Tag color={v === 'ACTIVE' ? 'success' : v === 'PAUSED' ? 'warning' : 'default'} bordered={false}>{v === 'ACTIVE' ? 'ĐANG CHẠY' : v === 'PAUSED' ? 'TẠM DẮNG' : v || '—'}</Tag>,
        },
        { key: "creative_id", title: "Creative ID", width: 160, render: (_: any, r: FbAd) => <span className="text-yellow-600 text-xs">{r.creative_id || '—'}</span> },
        { key: "impressions", title: "Hiển thị", width: 100, render: (_: any, r: FbAd) => fmtNum(r.insights?.impressions) },
        { key: "reach", title: "Tiếp cận", width: 100, render: (_: any, r: FbAd) => fmtNum(r.insights?.reach) },
        { key: "clicks", title: "Lượt click", width: 100, render: (_: any, r: FbAd) => fmtNum(r.insights?.clicks) },
        { key: "spend", title: "Chi phí", width: 140, render: (_: any, r: FbAd) => <span className="font-semibold text-yellow-600">{fmtMoney(r.insights?.spend)}</span> },
        { key: "ctr", title: "CTR", width: 80, render: (_: any, r: FbAd) => <span className="text-green-600">{fmtPct(r.insights?.ctr)}</span> },
        { key: "cpc", title: "CPC", width: 120, render: (_: any, r: FbAd) => <span className="text-blue-600">{fmtMoney(r.insights?.cpc)}</span> },
        { key: "cpm", title: "CPM", width: 120, render: (_: any, r: FbAd) => fmtMoney(r.insights?.cpm) },
        { key: "leads", title: "Leads", width: 80, render: (_: any, r: FbAd) => <span className="text-green-600 font-semibold">{fmtNum(r.insights?.leads)}</span> },
        { key: "cvr", title: "CVR", width: 80, render: (_: any, r: FbAd) => <span className="text-blue-600">{fmtPct(r.insights?.cvr)}</span> },
        { key: "cpl", title: "CPL/CPA", width: 130, render: (_: any, r: FbAd) => <span className="text-red-600">{fmtMoney(r.insights?.cpl)}</span> },
        { key: "revenue", title: "Doanh thu", width: 130, render: (_: any, r: FbAd) => <span className="text-green-600">{fmtMoney(r.insights?.revenue)}</span> },
        { key: "roas", title: "ROAS", width: 80, render: (_: any, r: FbAd) => r.insights?.roas == null ? '—' : Number(r.insights.roas).toFixed(2) },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data), mỗi bảng một key
    const prefixTable = useTableColumnSettings("campaign_prefix_list", columns, { scrollX: 700 });
    const campaignTable = useTableColumnSettings("fb_campaign_list", campaignColumns, { scrollX: 1600 });
    const adSetTable = useTableColumnSettings("fb_ad_set_list", adSetColumns, { scrollX: 1800 });
    const adTable = useTableColumnSettings("fb_ad_list", adColumns, { scrollX: 1800 });

    return (
        <Layout>

            <h2 className="text-xl font-bold text-gray-900 text-000000e0 mb-4">Quản lý tiền tố chiến dịch</h2>
            <Card
                className="mb-5"
            >
                <Row gutter={[16, 16]} align="bottom">

                    <Col xs={24} md={6}>
                        <label className="font-medium mb-2 block text-000000e0">
                            Tiền tố
                        </label>

                        <Input
                            placeholder="[SALE]"
                            value={prefix}
                            onChange={(e) => setPrefix(e.target.value)}
                            onPressEnter={handleSave}
                        />
                    </Col>

                    <Col xs={24} md={editingId ? 12 : 14}>
                        <label className="font-medium mb-2 block text-000000e0">
                            Mô tả
                        </label>

                        <TextArea
                            rows={1}
                            placeholder="Nhập mô tả..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </Col>

                    <Col xs={24} md={4}>
                        <Button
                            type="primary"
                            block
                            icon={<PlusOutlined />}
                            loading={saving}
                            onClick={handleSave}
                        >
                            {editingId ? "Cập nhật" : "Thêm mới"}
                        </Button>
                    </Col>

                    {editingId && (
                        <Col xs={24} md={2}>
                            <Button block icon={<CloseOutlined />} onClick={resetForm}>
                                Hủy
                            </Button>
                        </Col>
                    )}

                </Row>
            </Card>

            <Card
                title="Danh sách tiền tố"
                extra={
                    <div className="flex items-center gap-2">
                        <Input
                            allowClear
                            placeholder="Tìm tiền tố, mô tả..."
                            prefix={<Search size={15} className="text-slate-400" />}
                            className="w-full sm:w-[260px]"
                            value={searchInput}
                            onChange={(e) => { setPage(1); setSearchInput(e.target.value); }}
                        />
                        <ColumnSettingButton {...prefixTable.settingProps} />
                    </div>
                }
                styles={{
                    body: {
                        padding: 0,
                    },
                }}
            >
                <div className="mt-[1px]">
                    {loading ? <div className="p-4"><TableSkeleton rows={5} /></div> : (
                    <Table
                        rowKey="id"
                        columns={prefixTable.columns}
                        components={prefixTable.components}
                        tableLayout={prefixTable.tableLayout}
                        dataSource={data}
                        pagination={{
                            current: page,
                            pageSize: PER_PAGE,
                            total,
                            showSizeChanger: false,
                            onChange: (p) => setPage(p),
                            showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
                        }}
                        scroll={{ x: prefixTable.scrollX }}
                    />
                    )}
                </div>

            </Card>

            {/* Chiến dịch theo tiền tố */}
            <Drawer
                open={!!viewingPrefix}
                onClose={() => setViewingPrefix(null)}
                width="90%"
                title={
                    <div className="flex items-center gap-2 flex-wrap">
                        <span>Chiến dịch theo tiền tố</span>
                        <Tag color="blue">{viewingPrefix?.prefix}</Tag>
                        {viewingPrefix?.description && (
                            <span className="text-slate-400 text-sm font-normal">{viewingPrefix.description}</span>
                        )}
                    </div>
                }
            >
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <Input
                        allowClear
                        placeholder="Tìm theo tên hoặc ID chiến dịch..."
                        prefix={<Search size={15} className="text-slate-400" />}
                        className="w-full sm:w-[320px]"
                        value={campSearchInput}
                        onChange={(e) => { setCampPage(1); setCampSearchInput(e.target.value); }}
                    />
                    <Select
                        allowClear
                        placeholder="Tất cả trạng thái"
                        className="w-full sm:w-[200px]"
                        value={campStatus}
                        onChange={(v) => { setCampPage(1); setCampStatus(v); }}
                        options={[
                            { label: "Đang chạy", value: "ACTIVE" },
                            { label: "Tạm dừng", value: "PAUSED" },
                        ]}
                    />
                    <div className="sm:ml-auto">
                        <ColumnSettingButton {...campaignTable.settingProps} />
                    </div>
                </div>

                {campLoading ? <TableSkeleton rows={8} /> : (
                <Table
                    rowKey="id"
                    columns={campaignTable.columns}
                    components={campaignTable.components}
                    tableLayout={campaignTable.tableLayout}
                    dataSource={campaigns}
                    size="small"
                    pagination={{
                        current: campPage,
                        pageSize: CAMP_PER_PAGE,
                        total: campTotal,
                        showSizeChanger: false,
                        onChange: (p) => setCampPage(p),
                        showTotal: (t, range) => `${range[0]}-${range[1]} / ${t} chiến dịch`,
                    }}
                    scroll={{ x: campaignTable.scrollX }}
                />
                )}

                {/* Drawer cấp 2: Nhóm quảng cáo */}
                <Drawer
                    open={!!selectedCampaign}
                    onClose={() => setSelectedCampaign(null)}
                    width="88%"
                    title={
                        <div className="flex items-center gap-2 flex-wrap">
                            <span>Nhóm quảng cáo</span>
                            <Tag color="cyan">{selectedCampaign?.name}</Tag>
                        </div>
                    }
                    extra={<ColumnSettingButton {...adSetTable.settingProps} />}
                >
                    <MetricGrid insights={adSetsSummary} loading={adSetsLoading} />

                    {adSetsLoading ? <TableSkeleton rows={8} /> : (
                        <Table
                            rowKey="id"
                            columns={adSetTable.columns}
                            components={adSetTable.components}
                            tableLayout={adSetTable.tableLayout}
                            dataSource={adSets}
                            size="small"
                            pagination={{
                                current: adSetsPage,
                                pageSize: 50,
                                total: adSetsTotal,
                                showSizeChanger: false,
                                onChange: (p) => setAdSetsPage(p),
                                showTotal: (t, range) => `${range[0]}-${range[1]} / ${t} nhóm`,
                            }}
                            scroll={{ x: adSetTable.scrollX }}
                        />
                    )}

                    {/* Drawer cấp 3: Quảng cáo */}
                    <Drawer
                        open={!!selectedAdSet}
                        onClose={() => setSelectedAdSet(null)}
                        width="85%"
                        title={
                            <div className="flex items-center gap-2 flex-wrap">
                                <span>Quảng cáo</span>
                                <Tag color="geekblue">{selectedAdSet?.name}</Tag>
                            </div>
                        }
                        extra={<ColumnSettingButton {...adTable.settingProps} />}
                    >
                        <MetricGrid insights={adsSummary} loading={adsLoading} />

                        {adsLoading ? <TableSkeleton rows={8} /> : (
                            <Table
                                rowKey="id"
                                columns={adTable.columns}
                                components={adTable.components}
                                tableLayout={adTable.tableLayout}
                                dataSource={ads}
                                size="small"
                                pagination={{
                                    current: adsPage,
                                    pageSize: 50,
                                    total: adsTotal,
                                    showSizeChanger: false,
                                    onChange: (p) => setAdsPage(p),
                                    showTotal: (t, range) => `${range[0]}-${range[1]} / ${t} quảng cáo`,
                                }}
                                scroll={{ x: adTable.scrollX }}
                            />
                        )}
                    </Drawer>
                </Drawer>
            </Drawer>

        </Layout>
    );
}
